import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellInput,
  ETableCellSelect,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";
import {
  updateHtzReportCache,
  PatchMicrobialAssessmentRequest,
  useHtzReportPatchMicrobialAssessmentMutation,
  useHtzReportShowQuery,
} from "../api/report-api";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { MicrobialAssessment } from "../../inspection/types/microbial-assessment";
import { UUID } from "@bitory-gmbh/nidavellir";
import { AirHandlingUnitComponentName } from "../../ahu/components/air-handling-unit-data-designation";
import { patchMicrobialAssessment, Report } from "../types/report";
import {
  Result,
  ResultItems,
} from "../../inspection/components/inspection-result";
import t from "@/lang/lang";
import { Kind } from "@/services/backend/samples/types/kind";
import {
  updateAirMicrobialSampleSetCache,
  useSamplesAirMicrobialSampleSetPutMutation,
  useSamplesAirMicrobialSampleSetShowQuery,
} from "@/services/backend/samples/api/air-microbial-api";
import { MoldDifferentiationDialog } from "@/services/backend/samples/components/mold-differentiation-dialog";
import {
  updateSurfaceMicrobialSampleSetCache,
  useSamplesSurfaceMicrobialSampleSetPutMutation,
  useSamplesSurfaceMicrobialSampleSetShowQuery,
} from "@/services/backend/samples/api/surface-microbial-api";
import {
  updateDustDensitySampleCache,
  useSamplesDustDensitySamplePutMutation,
  useSamplesDustDensitySampleShowQuery,
} from "@/services/backend/samples/api/dust-density-sample-api";
import {
  updateWaterSampleCache,
  useSamplesWaterSamplePutMutation,
  useSamplesWaterSampleShowQuery,
} from "@/services/backend/samples/api/water-sample-api";
import { updateMeasurement } from "@/services/backend/samples/types/water-sample";
import { LaboratorySelect } from "@/services/backend/samples/components/laboratory-select";

export function MicrobialAssessmentsTable({ report }: { report: Report }) {
  const { data } = useHtzReportShowQuery({ id: report.id });

  if (!data) return;

  return (
    <ETable>
      <ETableHeader>
        <ETableRow>
          <ETableHead>{t("Komponente")}</ETableHead>
          <ETableHead>{t("Probennummer")}</ETableHead>
          <ETableHead>{t("Labor")}</ETableHead>
          <ETableHead colSpan={6}>{t("Werte")}</ETableHead>
          <ETableHead>{t("Ergebnis")}</ETableHead>
        </ETableRow>
      </ETableHeader>
      <ETableBody>
        <ETableRow>
          <ETableCell className="bg-secondary h-8 font-medium" colSpan={3}>
            {t("Luftkeimproben")}
          </ETableCell>
          <ETableCell className="bg-secondary">{t("Einheit")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Caso")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Malz")}</ETableCell>
          <ETableCell className="bg-secondary">{t("DG18")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Gesamt")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Schimmelpilze")}</ETableCell>
          <ETableCell className="bg-secondary"></ETableCell>
        </ETableRow>
        {data.microbialAssessments
          .filter((a) => a.kind === Kind.AirMicrobial)
          .map((assessment) => (
            <AirMicrobialSampleRow
              key={assessment.id}
              reportId={data.id}
              assessment={assessment}
            />
          ))}
        <ETableRow>
          <ETableCell className="bg-secondary h-8 font-medium" colSpan={3}>
            {t("Oberflächenkeimproben")}
          </ETableCell>
          <ETableCell className="bg-secondary">{t("Einheit")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Caso")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Malz")}</ETableCell>
          <ETableCell className="bg-secondary">{t("DG18")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Gesamt")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Schimmelpilze")}</ETableCell>
          <ETableCell className="bg-secondary"></ETableCell>
        </ETableRow>
        {data.microbialAssessments
          .filter((a) => a.sampleId != null && a.kind === Kind.SurfaceMicrobial)
          .map((assessment) => (
            <SurfaceMicrobialSampleRow
              key={assessment.id}
              reportId={data.id}
              assessment={assessment}
            />
          ))}
        <ETableRow>
          <ETableCell className="bg-secondary h-8 font-medium" colSpan={3}>
            {t("Staubdichtemessungen")}
          </ETableCell>
          <ETableCell className="bg-secondary">{t("Einheit")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Richtwert")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Konzentration")}</ETableCell>
          <ETableCell className="bg-secondary" colSpan={3}></ETableCell>
          <ETableCell className="bg-secondary"></ETableCell>
        </ETableRow>
        {data.microbialAssessments
          .filter((a) => a.sampleId != null && a.kind === Kind.DustDensity)
          .map((assessment) => (
            <DustDensitySampleRow
              key={assessment.id}
              reportId={data.id}
              assessment={assessment}
            />
          ))}
        <ETableRow>
          <ETableCell className="bg-secondary h-8 font-medium" colSpan={3}>
            {t("Wasserproben")}
          </ETableCell>
          <ETableCell className="bg-secondary">{t("Desinfiziert")}</ETableCell>
          <ETableCell className="bg-secondary">
            {t("Temperatur (°C)")}
          </ETableCell>
          <ETableCell className="bg-secondary" colSpan={4}>
            {t("Zählungen")}
          </ETableCell>
          <ETableCell className="bg-secondary"></ETableCell>
        </ETableRow>
        {data.microbialAssessments
          .filter((a) => a.sampleId != null && a.kind === Kind.Water)
          .map((assessment) => (
            <WaterSampleRow
              key={assessment.id}
              reportId={data.id}
              assessment={assessment}
            />
          ))}
      </ETableBody>
    </ETable>
  );
}

function AirMicrobialSampleRow({
  reportId,
  assessment,
}: {
  reportId: UUID;
  assessment: MicrobialAssessment;
}) {
  const { data } = useHtzReportShowQuery({ id: reportId });
  const request: PatchMicrobialAssessmentRequest = {
    id: reportId,
    assessmentId: assessment.id,
  };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzReportCache,
    useHtzReportPatchMicrobialAssessmentMutation(),
  );

  const { data: sample } = useSamplesAirMicrobialSampleSetShowQuery({
    id: assessment.sampleId!,
  });

  const [setSample] = useOptimisticDebouncedMutation(
    sample!,
    updateAirMicrobialSampleSetCache,
    useSamplesAirMicrobialSampleSetPutMutation(),
  );

  if (!data || !sample || !setSample) return;

  return (
    <ETableRow key={assessment.id}>
      <ETableCell className="font-medium">
        <AirHandlingUnitComponentName
          id={data.ahuDataId!}
          componentId={assessment.componentId}
        />
      </ETableCell>
      <ETableCell>{sample.number}</ETableCell>
      <ETableCell className="p-0">
        <LaboratorySelect
          labId={sample.labId}
          onLabIdChange={(labId) => setSample({ ...sample, labId })}
          className="rounded-none border-none shadow-none"
        />
      </ETableCell>
      <ETableCellInput
        value={sample.unit}
        onChange={(e) => setSample({ ...sample, unit: e.target.value })}
        disabled={data.immutable}
      />
      <ETableCellInput
        value={sample.caso?.value}
        onChange={(e) =>
          setSample({
            ...sample,
            caso: { ...sample.caso!, value: e.target.value },
          })
        }
        disabled={data.immutable}
      />
      <ETableCellInput
        value={sample.malt?.value}
        onChange={(e) =>
          setSample({
            ...sample,
            malt: { ...sample.malt!, value: e.target.value },
          })
        }
        disabled={data.immutable}
      />
      <ETableCellInput
        value={sample.dg18?.value}
        onChange={(e) =>
          setSample({
            ...sample,
            dg18: { ...sample.dg18!, value: e.target.value },
          })
        }
        disabled={data.immutable}
      />
      <ETableCellInput
        value={sample.value}
        onChange={(e) => setSample({ ...sample, value: e.target.value })}
        disabled={data.immutable}
      />
      <ETableCell className="p-0">
        <MoldDifferentiationDialog
          moldDifferentiation={sample.moldDifferentiation}
          onMoldDifferentiationChange={(moldDifferentiation) =>
            setSample({ ...sample, moldDifferentiation })
          }
          disabled={data.immutable}
        />
      </ETableCell>
      <ETableCellSelect
        value={assessment.result}
        onValueChange={(result) =>
          set(
            { ...request, result: result as Result },
            patchMicrobialAssessment(data, {
              ...assessment,
              result: result as Result,
            }),
          )
        }
        label={"Ergebnis"}
        items={ResultItems}
        disabled={data.immutable}
      />
    </ETableRow>
  );
}

function SurfaceMicrobialSampleRow({
  reportId,
  assessment,
}: {
  reportId: UUID;
  assessment: MicrobialAssessment;
}) {
  const { data } = useHtzReportShowQuery({ id: reportId });
  const request: PatchMicrobialAssessmentRequest = {
    id: reportId,
    assessmentId: assessment.id,
  };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzReportCache,
    useHtzReportPatchMicrobialAssessmentMutation(),
  );

  const { data: sample } = useSamplesSurfaceMicrobialSampleSetShowQuery({
    id: assessment.sampleId!,
  });

  const [setSample] = useOptimisticDebouncedMutation(
    sample!,
    updateSurfaceMicrobialSampleSetCache,
    useSamplesSurfaceMicrobialSampleSetPutMutation(),
  );

  if (!data || !sample || !setSample) return;

  return (
    <ETableRow key={assessment.id}>
      <ETableCell className="font-medium">
        <AirHandlingUnitComponentName
          id={data.ahuDataId!}
          componentId={assessment.componentId}
        />
      </ETableCell>
      <ETableCell>{sample.number}</ETableCell>
      <ETableCell className="p-0">
        <LaboratorySelect
          labId={sample.labId}
          onLabIdChange={(labId) => setSample({ ...sample, labId })}
          className="rounded-none border-none shadow-none"
        />
      </ETableCell>
      <ETableCellInput
        value={sample.unit}
        onChange={(e) => setSample({ ...sample, unit: e.target.value })}
        disabled={data.immutable}
      />
      <ETableCellInput
        value={sample.caso?.value}
        onChange={(e) =>
          setSample({
            ...sample,
            caso: { ...sample.caso!, value: e.target.value },
          })
        }
        disabled={data.immutable}
      />
      <ETableCellInput
        value={sample.malt?.value}
        onChange={(e) =>
          setSample({
            ...sample,
            malt: { ...sample.malt!, value: e.target.value },
          })
        }
        disabled={data.immutable}
      />
      <ETableCellInput
        value={sample.dg18?.value}
        onChange={(e) =>
          setSample({
            ...sample,
            dg18: { ...sample.dg18!, value: e.target.value },
          })
        }
        disabled={data.immutable}
      />
      <ETableCellInput
        value={sample.value}
        onChange={(e) => setSample({ ...sample, value: e.target.value })}
        disabled={data.immutable}
      />
      <ETableCell className="p-0">
        <MoldDifferentiationDialog
          moldDifferentiation={sample.moldDifferentiation}
          onMoldDifferentiationChange={(moldDifferentiation) =>
            setSample({ ...sample, moldDifferentiation })
          }
          disabled={data.immutable}
        />
      </ETableCell>
      <ETableCellSelect
        value={assessment.result}
        onValueChange={(result) =>
          set(
            { ...request, result: result as Result },
            patchMicrobialAssessment(data, {
              ...assessment,
              result: result as Result,
            }),
          )
        }
        label={"Ergebnis"}
        items={ResultItems}
        disabled={data.immutable}
      />
    </ETableRow>
  );
}

function DustDensitySampleRow({
  reportId,
  assessment,
}: {
  reportId: UUID;
  assessment: MicrobialAssessment;
}) {
  const { data } = useHtzReportShowQuery({ id: reportId });
  const request: PatchMicrobialAssessmentRequest = {
    id: reportId,
    assessmentId: assessment.id,
  };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzReportCache,
    useHtzReportPatchMicrobialAssessmentMutation(),
  );

  const { data: sample } = useSamplesDustDensitySampleShowQuery({
    id: assessment.sampleId!,
  });

  const [setSample] = useOptimisticDebouncedMutation(
    sample!,
    updateDustDensitySampleCache,
    useSamplesDustDensitySamplePutMutation(),
  );

  if (!data || !sample || !setSample) return;

  return (
    <ETableRow key={assessment.id}>
      <ETableCell className="font-medium">
        <AirHandlingUnitComponentName
          id={data.ahuDataId!}
          componentId={assessment.componentId}
        />
      </ETableCell>
      <ETableCell>{sample.number}</ETableCell>
      <ETableCell className="p-0">
        <LaboratorySelect
          labId={sample.labId}
          onLabIdChange={(labId) => setSample({ ...sample, labId })}
          className="rounded-none border-none shadow-none"
        />
      </ETableCell>
      <ETableCellInput
        value={sample.unit}
        onChange={(e) => setSample({ ...sample, unit: e.target.value })}
        disabled={data.immutable}
      />
      <ETableCellInput
        value={sample.referenceValue}
        onChange={(e) =>
          setSample({ ...sample, referenceValue: e.target.value })
        }
        disabled={data.immutable}
      />
      <ETableCellInput
        value={sample.value}
        onChange={(e) => setSample({ ...sample, value: e.target.value })}
        disabled={data.immutable}
      />
      <ETableCell colSpan={3}></ETableCell>
      <ETableCellSelect
        value={assessment.result}
        onValueChange={(result) =>
          set(
            { ...request, result: result as Result },
            patchMicrobialAssessment(data, {
              ...assessment,
              result: result as Result,
            }),
          )
        }
        label={"Ergebnis"}
        items={ResultItems}
        disabled={data.immutable}
      />
    </ETableRow>
  );
}

function WaterSampleRow({
  reportId,
  assessment,
}: {
  reportId: UUID;
  assessment: MicrobialAssessment;
}) {
  const { data } = useHtzReportShowQuery({ id: reportId });
  const request: PatchMicrobialAssessmentRequest = {
    id: reportId,
    assessmentId: assessment.id,
  };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzReportCache,
    useHtzReportPatchMicrobialAssessmentMutation(),
  );

  const { data: sample } = useSamplesWaterSampleShowQuery({
    id: assessment.sampleId!,
  });

  const [setSample] = useOptimisticDebouncedMutation(
    sample!,
    updateWaterSampleCache,
    useSamplesWaterSamplePutMutation(),
  );

  if (!data || !sample || !setSample) return;

  return (
    <>
      <ETableRow key={assessment.id}>
        <ETableCell
          className="align-top font-medium"
          rowSpan={sample.measurements.length + 1}
        >
          <div className="pt-2">
            <AirHandlingUnitComponentName
              id={data.ahuDataId!}
              componentId={assessment.componentId}
            />
          </div>
        </ETableCell>
        <ETableCell
          className="align-top"
          rowSpan={sample.measurements.length + 1}
        >
          <div className="pt-2">{sample.number}</div>
        </ETableCell>
        <ETableCell
          className="p-0 align-top"
          rowSpan={sample.measurements.length + 1}
        >
          <LaboratorySelect
            labId={sample.labId}
            onLabIdChange={(labId) => setSample({ ...sample, labId })}
            className="rounded-none border-none shadow-none"
          />
        </ETableCell>
        <ETableCellSelect
          label={"Desinfiziert"}
          value={sample.desinfected ? "true" : "false"}
          onValueChange={(desinfected) =>
            setSample({ ...sample, desinfected: desinfected === "true" })
          }
          items={[
            { label: t("Ja"), value: "true" },
            { label: t("Nein"), value: "false" },
          ]}
        />
        <ETableCellInput
          type="number"
          value={sample.temperature}
          onChange={(e) =>
            setSample({ ...sample, temperature: Number(e.target.value) })
          }
          cellClassName="align-top"
          disabled={data.immutable}
        />
        <ETableCell colSpan={1}>{t("Wert")}</ETableCell>
        <ETableCell colSpan={1}>{t("Referenz")}</ETableCell>
        <ETableCell colSpan={2}>{t("Einheit")}</ETableCell>
        <ETableCellSelect
          value={assessment.result}
          onValueChange={(result) =>
            set(
              { ...request, result: result as Result },
              patchMicrobialAssessment(data, {
                ...assessment,
                result: result as Result,
              }),
            )
          }
          label={"Ergebnis"}
          items={ResultItems}
          cellClassName="align-top"
          rowSpan={sample.measurements.length + 1}
          disabled={data.immutable}
        />
      </ETableRow>
      {sample.measurements.map((m) => (
        <ETableRow key={m.order}>
          <ETableCellInput
            colSpan={2}
            value={m.name}
            onChange={(e) => {
              setSample(
                updateMeasurement(sample, { ...m, name: e.target.value }),
              );
            }}
          />
          <ETableCellInput
            className="w-20"
            value={m.value}
            onChange={(e) => {
              setSample(
                updateMeasurement(sample, { ...m, value: e.target.value }),
              );
            }}
          />
          <ETableCellInput
            className="w-20"
            value={m.referenceValue}
            onChange={(e) => {
              setSample(
                updateMeasurement(sample, {
                  ...m,
                  referenceValue: e.target.value,
                }),
              );
            }}
          />
          <ETableCellInput
            colSpan={2}
            value={m.unit}
            onChange={(e) => {
              setSample(
                updateMeasurement(sample, { ...m, unit: e.target.value }),
              );
            }}
          />
        </ETableRow>
      ))}
    </>
  );
}
